import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Settings as SettingsIcon,
  Globe,
  ChevronRight,
  User,
  LogOut,
  Shield,
  Bell,
  PanelRight,
  CircleUser,
  Sparkles,
  Trophy,
  Palette,
  Lock,
  Mail,
  DollarSign
} from "lucide-react";
import { motion } from "framer-motion";
import SubscriptionManagement from "../subscription/SubscriptionManagement";
import ConfirmationModal from "../ui/ConfirmationModal";
import { getAuth } from "firebase/auth";
import { createTranslator } from '../../utils/detectLanguage';
import { settingsPtBR } from '../mappings/settings.pt-br';
import { settingsES } from '../mappings/settings.es';
// Import components
import UserGamificationDashboard from '../gamification/UserGamificationDashboard';
import StyledBettingWrapper from './StyledBettingWrapper';
import LanguageSettings from './LanguageSettings';

// Import modular components
import MobileBottomNav from './MobileBottomNav';
import MobileHeader from './MobileHeader';
import MobileSideMenu from './MobileSideMenu';
import SwipeIndicator from './SwipeIndicator';
import SectionIndicator from './SectionIndicator';

const Settings = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const mainContentRef = useRef(null);

  // State management
  const [activeSection, setActiveSection] = useState("account");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeProgress, setSwipeProgress] = useState(0);

  // Translation helper
  const t = createTranslator({
    'pt-br': settingsPtBR,
    'es': settingsES
  });

  // Navigation items - Modern sections (reordered to prioritize universal features)
  const navItems = [
    {
      id: "account",
      label: t("Profile"),
      icon: <CircleUser className="w-5 h-5" />,
      description: t("Update your personal information and preferences")
    },
    {
      id: "gamification",
      label: t("Achievements"),
      icon: <Trophy className="w-5 h-5" />,
      description: t("Track your achievements and rewards"),
      customStyle: true,
      iconColor: "text-amber-300",
      activeColor: "bg-amber-500/20",
      borderColor: "border-amber-400/30",
      labelColor: "text-amber-200",
    },
    {
      id: "subscription",
      label: t("Subscription"),
      icon: <Sparkles className="w-5 h-5" />,
      description: t("Manage your subscription plan and billing")
    },
    {
      id: "notifications",
      label: t("Notifications"),
      icon: <Bell className="w-5 h-5" />,
      description: t("Customize your notification preferences")
    },
    {
      id: "betting",
      label: t("Bet History"),
      icon: <DollarSign className="w-5 h-5" />,
      description: t("Your betting analytics and history"),
      customStyle: true,
      iconColor: "text-green-400",
      activeColor: "bg-green-500/20",
      borderColor: "border-green-400/30",
      labelColor: "text-green-200",
    },
    {
      id: "privacy",
      label: t("Privacy & Security"),
      icon: <Shield className="w-5 h-5" />,
      description: t("Manage your privacy settings and security options")
    },
    {
      id: "appearance",
      label: t("Appearance"),
      icon: <PanelRight className="w-5 h-5" />,
      description: t("Customize the look and feel of your experience")
    },
    {
      id: "language",
      label: t("Language"),
      icon: <Globe className="w-5 h-5" />,
      description: t("Choose your preferred language")
    }
  ];

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  // Enhanced swipe gestures with momentum and visual feedback
  const handleTouchStart = useCallback((e) => {
    const touch = e.targetTouches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchEnd(null);
    setIsSwiping(false);
    setSwipeProgress(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStart) return;

    const touch = e.targetTouches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;

    // Calculate distances
    const deltaX = currentX - touchStart.x;
    const deltaY = currentY - touchStart.y;

    // Only consider horizontal swipes (ignore if vertical movement is dominant)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setIsSwiping(true);

      // Calculate progress (0-100) for visual feedback
      const progress = Math.min(Math.abs(deltaX) / 100, 1) * 100;
      setSwipeProgress(progress);

      // Prevent default scroll behavior during horizontal swipe
      if (Math.abs(deltaX) > 20) {
        e.preventDefault();
      }
    }

    setTouchEnd({
      x: currentX,
      y: currentY,
      time: Date.now()
    });
  }, [touchStart]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      setIsSwiping(false);
      setSwipeProgress(0);
      return;
    }

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const deltaTime = touchEnd.time - touchStart.time;

    // Calculate velocity (pixels per millisecond)
    const velocity = Math.abs(deltaX) / deltaTime;

    // Only process horizontal swipes
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Swipe threshold based on distance OR velocity (momentum)
      const minSwipeDistance = 50;
      const minSwipeVelocity = 0.3; // pixels per ms

      const isLeftSwipe = deltaX > minSwipeDistance || (deltaX > 20 && velocity > minSwipeVelocity);
      const isRightSwipe = deltaX < -minSwipeDistance || (deltaX < -20 && velocity > minSwipeVelocity);

      const currentIndex = navItems.findIndex(item => item.id === activeSection);

      if (isLeftSwipe && currentIndex < navItems.length - 1) {
        // Navigate to next section
        setActiveSection(navItems[currentIndex + 1].id);
        // Simulate haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      } else if (isRightSwipe && currentIndex > 0) {
        // Navigate to previous section
        setActiveSection(navItems[currentIndex - 1].id);
        // Simulate haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }
    }

    // Reset swipe state
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
    setSwipeProgress(0);
  }, [touchStart, touchEnd, navItems, activeSection]);

  const activeItem = navItems.find(item => item.id === activeSection);

  // Add non-passive touch event listeners to allow preventDefault
  useEffect(() => {
    const element = mainContentRef.current;
    if (!element) return;

    // Add event listeners with passive: false to allow preventDefault
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]); // Re-attach when handlers change

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Glass overlay effect */}
      <div className="fixed inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav
        navItems={navItems}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
      />
      
      <div className="relative max-w-screen-xl mx-auto px-4 py-4 md:px-6 md:py-8 pb-20 md:pb-8">
        {/* Modern Header */}
        {/* Mobile Header */}
        <MobileHeader
          activeSection={activeSection}
          navItems={navItems}
          user={user}
          mobileMenuOpen={mobileMenuOpen}
          onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
          onLogout={() => setShowLogoutModal(true)}
        />

        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-4 md:mb-8"
        >

          {/* Desktop Header */}
          <div className="hidden md:block bg-white/[0.02] backdrop-blur-sm p-6 md:p-8 rounded-lg border border-white/[0.05]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left section with icon and title */}
              <div className="flex items-center gap-4">
                <div className="p-3 md:p-4 bg-white/[0.03] backdrop-blur-sm rounded-md border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-200">
                  <SettingsIcon className="w-6 h-6 md:w-8 md:h-8 text-emerald-400/80" />
                </div>
                <div>
                  <h1 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">
                    {t("Configuration")}
                  </h1>
                  <p className="text-2xl md:text-3xl font-sans text-white/95">
                    {t("Settings")}
                  </p>
                  <p className="text-sm text-white/50 mt-1">
                    {t("Personalize your PickStar experience")}
                  </p>
                </div>
              </div>
              
              {/* User avatar only */}
              {user && (
                <div className="hidden md:block">
                  <div className="relative">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt="Profile"
                        className="w-10 h-10 rounded-md border border-white/[0.05]"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-white/[0.02] rounded-md flex items-center justify-center border border-white/[0.05]">
                        <span className="font-mono text-sm text-emerald-400/80">
                          {user.displayName ? user.displayName[0].toUpperCase() : user.email[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.header>

        {/* Mobile Side Menu */}
        <MobileSideMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          navItems={navItems}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          user={user}
          t={t}
        />

        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          {/* Modern Desktop Sidebar Navigation */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="hidden md:block md:w-80 bg-white/[0.02] backdrop-blur-sm rounded-lg overflow-hidden border border-white/[0.05]"
          >
            <div className="p-5 border-b border-white/[0.05]">
              <h3 className="text-[11px] font-medium tracking-[0.25em] text-white/40 uppercase">{t("Navigation")}</h3>
            </div>
            <div className="p-3">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                >
                  <button
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full mb-1 group relative overflow-hidden rounded-md transition-all duration-200 ${
                      activeSection === item.id
                        ? ""
                        : "hover:bg-white/[0.02]"
                    }`}
                  >
                    {/* Active indicator */}
                    {activeSection === item.id && (
                      <motion.div
                        layoutId="activeTab"
                        className={`absolute inset-0 ${
                          item.customStyle
                            ? "bg-gradient-to-r from-amber-500/[0.02] to-violet-500/[0.02]"
                            : "bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02]"
                        } rounded-md`}
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    
                    <div className="relative flex items-center justify-between px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded transition-colors duration-200 ${
                            activeSection === item.id
                              ? item.customStyle
                                ? "bg-white/[0.05]"
                                : "bg-white/[0.03]"
                              : "bg-white/[0.02] group-hover:bg-white/[0.03]"
                          }`}
                        >
                          <div className={
                            activeSection === item.id
                              ? item.customStyle ? "text-amber-400/80" : "text-emerald-400/80"
                              : "text-white/30 group-hover:text-white/50"
                          }>
                            {React.cloneElement(item.icon, { className: "w-4 h-4" })}
                          </div>
                        </div>
                        <div className="text-left">
                          <div className={`text-sm font-sans transition-colors duration-200 ${
                            activeSection === item.id
                              ? "text-white/80"
                              : "text-white/50 group-hover:text-white/70"
                          }`}>
                            {item.label}
                          </div>
                          <div className={`text-[11px] mt-0.5 transition-colors duration-200 ${
                            activeSection === item.id
                              ? "text-white/50"
                              : "text-white/30 group-hover:text-white/40"
                          }`}>
                            {item.description}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 transition-all duration-200 ${
                          activeSection === item.id
                            ? item.customStyle ? "text-amber-400/70" : "text-emerald-400/70"
                            : "text-white/20 group-hover:text-white/30"
                        } ${
                          activeSection === item.id ? "translate-x-1" : ""
                        }`} />
                    </div>
                  </button>
                </motion.div>
              ))}
            </div>
            
            {/* User Info Section */}
            {user && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mt-auto p-4 border-t border-white/[0.05] bg-white/[0.02]"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="Profile" className="w-11 h-11 rounded-md border border-white/[0.05]" />
                    ) : (
                      <div className="w-11 h-11 bg-white/[0.02] rounded-md flex items-center justify-center border border-white/[0.05]">
                        <span className="font-mono text-sm text-emerald-400/80">
                          {user.displayName ? user.displayName[0].toUpperCase() : user.email[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/70 truncate">
                      {user.displayName || t("User")}
                    </div>
                    <div className="text-xs text-white/30 truncate">{user.email}</div>
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="p-2.5 text-red-400/70 hover:text-red-400/90 hover:bg-white/[0.02] rounded-md transition-all duration-200"
                    title={t("Sign Out")}
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </motion.nav>

          {/* Mobile Content */}
          <div className="flex-1 md:hidden mt-16">
            {/* Mobile Swipe Indicator */}
            <SwipeIndicator
              isSwiping={isSwiping}
              swipeProgress={swipeProgress}
              currentIndex={navItems.findIndex(item => item.id === activeSection)}
              totalSections={navItems.length}
            />
          </div>

          {/* Content Area - Mobile optimized */}
          <motion.main
            ref={mainContentRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 bg-white/[0.02] backdrop-blur-sm rounded-lg overflow-hidden border border-white/[0.05] md:bg-white/[0.02] relative"
          >
            {/* Premium thin swipe progress indicator */}
            {isSwiping && swipeProgress > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="md:hidden absolute top-0 left-0 right-0 h-[2px] bg-white/[0.03] z-50"
              >
                <motion.div
                  className="h-full bg-gradient-to-r from-emerald-500/70 to-violet-500/70 rounded-r-full"
                  style={{ width: `${swipeProgress}%` }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                />
              </motion.div>
            )}
            {/* Desktop Header */}
            <motion.div
              key={`header-${activeSection}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="hidden md:block p-5 md:p-6 border-b border-white/[0.05] bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02]"
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/[0.03] rounded-md border border-white/[0.05]">
                    <div className={activeItem?.customStyle ? "text-amber-400/80" : "text-emerald-400/80"}>
                      {React.cloneElement(activeItem?.icon || <SettingsIcon />, { className: "w-6 h-6" })}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-[11px] font-medium tracking-[0.25em] text-white/40 uppercase mb-1">
                      {navItems.find(item => item.id === activeSection)?.label || "Settings"}
                    </h2>
                    <p className="text-sm text-white/70">
                      {navItems.find(item => item.id === activeSection)?.description || "Manage your preferences"}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mobile Section Indicator */}
            <SectionIndicator navItems={navItems} activeSection={activeSection} />
            
            <motion.div
              key={`content-${activeSection}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ type: "spring", damping: 20 }}
              className="p-4 md:p-6 overflow-y-auto max-h-[calc(100vh-200px)] md:max-h-[calc(100vh-300px)]"
            >
              {/* Betting History Section - Using Original Component */}
              {activeSection === "betting" && (
                <div className="space-y-6">
                  {user ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <StyledBettingWrapper />
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-white/[0.02] rounded-lg p-12 border border-white/[0.05] text-center"
                    >
                      <DollarSign className="w-16 h-16 text-emerald-400/80 mx-auto mb-4" />
                      <p className="text-sm text-white/50">{t("Sign in to view your betting history")}</p>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Gamification/Achievements Section */}
              {activeSection === "gamification" && (
                <div className="space-y-6">
                  {user ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <UserGamificationDashboard userId={user.uid} />
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-white/[0.02] rounded-lg p-12 border border-white/[0.05] text-center"
                    >
                      <Sparkles className="w-16 h-16 text-violet-400/80 mx-auto mb-4" />
                      <p className="text-sm text-white/50">{t("Sign in to view your achievements")}</p>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Subscription Section */}
              {activeSection === "subscription" && (
                <div className="space-y-6">
                  <SubscriptionManagement />
                </div>
              )}
              
              {/* Account Section with Modern Cards */}
              {activeSection === "account" && (
                <div className="space-y-6">
                  {user ? (
                    <>
                      {/* Personal Info Card */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white/[0.03] backdrop-blur-sm rounded-lg p-6 border border-white/[0.05]"
                      >
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-white/[0.03] rounded">
                            <User className="w-4 h-4 text-emerald-400/80" />
                          </div>
                          <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">{t("Personal Information")}</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-[10px] tracking-[0.2em] text-white/30 uppercase mb-2">{t("Full Name")}</label>
                            <input
                              type="text"
                              defaultValue={user.displayName || ""}
                              className="w-full bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md px-4 py-3 text-sm text-white/80 placeholder-white/30 focus:bg-white/[0.03] focus:border-emerald-500/30 focus:outline-none transition-all duration-200"
                              placeholder={t("Enter your name")}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] tracking-[0.2em] text-white/30 uppercase mb-2">{t("Email Address")}</label>
                            <div className="relative">
                              <input
                                type="email"
                                defaultValue={user.email || ""}
                                readOnly
                                className="w-full bg-white/[0.02] border border-white/[0.05] rounded-md px-4 py-3 pr-10 text-sm text-white/50 cursor-not-allowed"
                              />
                              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                          <button className="px-6 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200">
                            {t("Save Changes")}
                          </button>
                        </div>
                      </motion.div>

                      {/* Preferences Card */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white/[0.03] backdrop-blur-sm rounded-lg p-6 border border-white/[0.05]"
                      >
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-white/[0.03] rounded">
                            <SettingsIcon className="w-4 h-4 text-emerald-400/80" />
                          </div>
                          <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">{t("Account Preferences")}</h3>
                        </div>
                        <div className="space-y-5">
                          <div className="flex items-center justify-between p-4 bg-white/[0.02] rounded-md border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/[0.02] rounded">
                                <Mail className="w-4 h-4 text-emerald-400/80" />
                              </div>
                              <div>
                                <h4 className="text-sm text-white/70 group-hover:text-white/80">{t("Email Notifications")}</h4>
                                <p className="text-xs text-white/30 mt-0.5">{t("Receive updates and alerts")}</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input type="checkbox" className="sr-only peer" defaultChecked />
                              <div className="w-11 h-6 bg-white/[0.05] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white/80 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500/80"></div>
                            </label>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-white/[0.02] rounded-md border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/[0.02] rounded">
                                <Shield className="w-4 h-4 text-emerald-400/80" />
                              </div>
                              <div>
                                <h4 className="text-sm text-white/70 group-hover:text-white/80">{t("Two-Factor Authentication")}</h4>
                                <p className="text-xs text-white/30 mt-0.5">{t("Enhanced account security")}</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input type="checkbox" className="sr-only peer" />
                              <div className="w-11 h-6 bg-white/[0.05] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white/80 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500/80"></div>
                            </label>
                          </div>
                        </div>
                      </motion.div>
                    </>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-white/[0.02] rounded-lg p-12 border border-white/[0.05] text-center"
                    >
                      <CircleUser className="w-16 h-16 text-emerald-400/80 mx-auto mb-4" />
                      <p className="text-sm text-white/50">{t("Sign in to manage your account settings")}</p>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Language Section */}
              {activeSection === "language" && (
                <motion.div
                  key="language"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <LanguageSettings t={t} />
                </motion.div>
              )}

              {/* Coming Soon Sections */}
              {(activeSection === "notifications" || activeSection === "privacy" || activeSection === "appearance") && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white/[0.02] rounded-lg p-12 md:p-16 border border-white/[0.05] flex flex-col items-center justify-center text-center"
                >
                  <div className="p-4 bg-white/[0.03] rounded-md mb-6 border border-white/[0.05]">
                    {activeSection === "notifications" && <Bell className="w-8 h-8 text-emerald-400/80" />}
                    {activeSection === "privacy" && <Shield className="w-8 h-8 text-emerald-400/80" />}
                    {activeSection === "appearance" && <Palette className="w-8 h-8 text-emerald-400/80" />}
                  </div>
                  <h3 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-3">{t("Coming Soon")}</h3>
                  <p className="text-sm text-white/50 max-w-sm">
                    {t("We're crafting an amazing experience for this feature. Stay tuned for updates!")}
                  </p>
                  <div className="mt-8 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse"></div>
                    <div className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse delay-150"></div>
                    <div className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse delay-300"></div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.main>
        </div>
      </div>
      
      {/* Enhanced Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          auth.signOut();
          setShowLogoutModal(false);
        }}
        message={t("Are you sure you want to log out of your account?")}
      />
    </div>
  );
};

export default Settings;